import axios from 'axios';
import type { ActivityTimeline, AttentionItem, PortfolioBoard, TriageResult, WorkQueue } from '../types/board';
import type {
  AcknowledgementRequest as AbfAcknowledgementRequest,
  EvidencePackRef,
  FacilityRecipients,
  Recipient,
  RecipientBinding,
  RecipientRegistry,
  RecipientRequest,
  RegisterFileResult,
  ReportDelivery,
  ReportDetail,
  ReportHandoff,
  ReportReadiness,
  ReportingPolicy,
  ReportingWorkspaceModel,
  VerifyResult,
} from '../types/reporting';
import type { DistributionPage } from '../types/distribution';
import type { AccountsBoard, FacilityDocuments, FacilityWorkspaceOverview } from '../types/facilityWorkspace';
import type { TapeSurveillance } from '../types/tapeSurveillance';
import type { ObligorAlias, ObligorMasterList, ObligorMergeRequest } from '../types/obligors';
import type { ActivationRequest, FacilityContractVersion, ManualAmendmentRequest } from '../types/contractWorkspace';
import type {
  AmendmentReview, FacilityCandidate, FrozenAmendmentReview, TermCatalogueEntry, VersionAsKnownAt,
} from '../types/amendmentReview';
import type { ActivationReadiness, CitationInput } from '../types/activationReadiness';
import type {
  AbfDeliveryUploadRequest,
  AbfIngestionCenterReadModel,
  AbfIngestionDelivery,
} from '../types/ingestionCenter';
import type {
  CollateralAssetDetail,
  CollateralExplorerQuery,
  CollateralExplorerReadModel,
  CollateralMovementRun,
  EligibilityRuleSet,
  EligibilityRuleUpdate,
  PoolSeries,
  ReleasedTapeComparison,
  RuleSimulationRun,
  RuleWorkbenchReadModel,
  StratDimension,
  StratResponse,
} from '../types/collateralExplorer';
import type {
  BbcComparisonRequest,
  BorrowingBaseRun,
  BorrowingBaseWorkspaceReadModel,
} from '../types/borrowingBase';
import type {
  ClosingAdvanceReadiness,
  ConfirmFundingRequest,
  CreateDrawRequest,
  CreateRepaymentRequest,
  DrawRequestRecord,
  FacilityHolders,
  FundingGateEvaluation,
  FundingPolicy,
  FundingWorkspaceReadModel,
  ReleaseDrawRequest,
  RepaymentKind,
  ReversalProposal,
} from '../types/funding';
import type {
  ContractOperationsReadModel,
  CovenantCalculationResult,
  CovenantRun,
  CovenantWaiver,
  MonitoringWorkspaceReadModel,
  OutboundExchange,
  ReconciliationReadModel,
  ReportInstance,
  ReportingWorkspaceReadModel,
  ScenarioDefinition,
  ScenarioRun,
  ValuationPriceEvidence,
  ValuationRun,
  WaterfallRun,
} from '../types/round3';

const ABF_API_ROOT = '/api/credit/abf';

export const getFacilityWorkspaceOverview = async (
  facilityId: number,
): Promise<FacilityWorkspaceOverview> => {
  const response = await axios.get<FacilityWorkspaceOverview>(
    `${ABF_API_ROOT}/facilities/${facilityId}/workspace-overview`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const getFacilityContractVersions = async (
  facilityId: number,
): Promise<FacilityContractVersion[]> => {
  const response = await axios.get<FacilityContractVersion[]>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/versions`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const getFacilityContractVersion = async (
  versionId: number,
): Promise<FacilityContractVersion> => {
  const response = await axios.get<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const getFacilityVersionWorkQueue = async (): Promise<FacilityContractVersion[]> => {
  const response = await axios.get<FacilityContractVersion[]>(
    `${ABF_API_ROOT}/round2/facility-versions`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const activateFacilityContractVersion = async (
  versionId: number,
  request: ActivationRequest = {},
): Promise<FacilityContractVersion> => {
  const response = await axios.post<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/activate`,
    request,
  );
  return response.data;
};

export const stageManualFacilityAuthority = async (
  facilityId: number,
  request: Record<string, unknown>,
): Promise<FacilityContractVersion> => {
  const response = await axios.post<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/manual-authority`,
    request,
  );
  return response.data;
};

export const stageManualFacilityAmendment = async (
  facilityId: number,
  request: ManualAmendmentRequest,
): Promise<FacilityContractVersion> => {
  const response = await axios.post<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/manual-amendments`, request,
  );
  return response.data;
};

// ---------------------------------------------------------------- Slice 6 amendments

export const getAmendmentReview = async (versionId: number): Promise<AmendmentReview> => {
  const response = await axios.get<AmendmentReview>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/amendment-review`, { params: { _: Date.now() } },
  );
  return response.data;
};

export const getFrozenAmendmentReview = async (versionId: number): Promise<FrozenAmendmentReview> => {
  const response = await axios.get<FrozenAmendmentReview>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/amendment-review/frozen`, { params: { _: Date.now() } },
  );
  return response.data;
};

export const approveAmendment = async (
  versionId: number, request: { notes?: string; acknowledgements: string[] },
): Promise<AmendmentReview> => {
  const response = await axios.post<AmendmentReview>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/approve`, request,
  );
  return response.data;
};

export const activateAmendmentNow = async (versionId: number): Promise<AmendmentReview> => {
  const response = await axios.post<AmendmentReview>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/activate-now`,
  );
  return response.data;
};

export const withdrawAmendment = async (versionId: number, reason: string): Promise<FacilityContractVersion> => {
  const response = await axios.post<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/withdraw`, { reason },
  );
  return response.data;
};

export const getAmendmentFacilityCandidates = async (versionId: number): Promise<FacilityCandidate[]> => {
  const response = await axios.get<FacilityCandidate[]>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/facility-candidates`, { params: { _: Date.now() } },
  );
  return response.data;
};

export const resolveAmendmentFacility = async (
  versionId: number, request: { facilityId: number; reason: string },
): Promise<FacilityContractVersion> => {
  const response = await axios.post<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/resolve-facility`, request,
  );
  return response.data;
};

export const getAmendmentTermCatalogue = async (): Promise<TermCatalogueEntry[]> => {
  const response = await axios.get<TermCatalogueEntry[]>(`${ABF_API_ROOT}/round2/amendments/term-catalogue`);
  return response.data;
};

export const resolveFacilityVersionAsKnownAt = async (
  facilityId: number, effectiveAt: string, knownAt: string,
): Promise<VersionAsKnownAt> => {
  const response = await axios.get<VersionAsKnownAt>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/versions/resolve`,
    { params: { effectiveAt, knownAt, _: Date.now() } },
  );
  return response.data;
};

export const stageRuleClassificationCorrection = async (versionId: number): Promise<FacilityContractVersion> => {
  const response = await axios.post<FacilityContractVersion>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/rule-classification-correction`,
  );
  return response.data;
};

export const getFacilityIngestionCenter = async (
  facilityId: number,
): Promise<AbfIngestionCenterReadModel> => {
  const response = await axios.get<AbfIngestionCenterReadModel>(
    `${ABF_API_ROOT}/facilities/${facilityId}/ingestion-center`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const uploadFacilityDelivery = async (
  facilityId: number,
  request: AbfDeliveryUploadRequest,
): Promise<AbfIngestionDelivery & { message?: string; duplicate?: boolean }> => {
  const form = new FormData();
  form.append('file', request.file);
  form.append('asOfDate', request.asOfDate);
  form.append('channel', 'UI_UPLOAD');
  if (request.vendorName) form.append('vendorName', request.vendorName);
  if (request.servicerName) form.append('servicerName', request.servicerName);
  const response = await axios.post<AbfIngestionDelivery & { message?: string; duplicate?: boolean }>(
    `${ABF_API_ROOT}/facilities/${facilityId}/ingestion-center/deliveries`,
    form,
  );
  return response.data;
};

export const replayFacilityDelivery = async (
  facilityId: number,
  manifestId: number,
): Promise<AbfIngestionDelivery & { message?: string; duplicate?: boolean }> => {
  const response = await axios.post<AbfIngestionDelivery & { message?: string; duplicate?: boolean }>(
    `${ABF_API_ROOT}/facilities/${facilityId}/ingestion-center/deliveries/${manifestId}/replay`,
  );
  return response.data;
};

export const releaseFacilityTape = async (
  facilityId: number,
  tapeIngestId: number,
  notes: string,
  expectedPeriodId: number,
  expectedAsOfDate: string,
): Promise<Record<string, any>> => {
  const response = await axios.post<Record<string, any>>(
    `${ABF_API_ROOT}/facilities/${facilityId}/tapes/${tapeIngestId}/release`,
    { notes, expectedPeriodId, expectedAsOfDate, scopeConfirmed: true },
  );
  return response.data;
};

export const getCollateralExplorer = async (
  facilityId: number,
  query: CollateralExplorerQuery = {},
): Promise<CollateralExplorerReadModel> => {
  const response = await axios.get<CollateralExplorerReadModel>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/collateral-explorer',
    { params: { ...query, _: Date.now() } },
  );
  return response.data;
};

export const getCollateralAssetDetail = async (
  facilityId: number,
  tapeIngestId: number,
  assetId: number,
): Promise<CollateralAssetDetail> => {
  const response = await axios.get<CollateralAssetDetail>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/collateral-explorer/assets/' + assetId,
    { params: { tapeIngestId, _: Date.now() } },
  );
  return response.data;
};

export const compareReleasedCollateralTapes = async (
  facilityId: number,
  baseTapeIngestId: number,
  comparisonTapeIngestId: number,
): Promise<ReleasedTapeComparison> => {
  const response = await axios.get<ReleasedTapeComparison>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/collateral-explorer/compare',
    { params: { baseTapeIngestId, comparisonTapeIngestId, _: Date.now() } },
  );
  return response.data;
};

export const reconcileReleasedCollateralMovements = async (
  facilityId: number,
  currentTapeIngestId: number,
  priorTapeIngestId: number,
): Promise<CollateralMovementRun> => {
  const response = await axios.post<CollateralMovementRun>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/collateral-explorer/movements/reconcile',
    { currentTapeIngestId, priorTapeIngestId },
  );
  return response.data;
};

// WS4: facility documents (live union over the workflow tables plus free uploads) and the accounts board.
export const getFacilityDocuments = async (facilityId: number): Promise<FacilityDocuments> =>
  (await axios.get<FacilityDocuments>(ABF_API_ROOT + '/facilities/' + facilityId + '/documents', { params: { _: Date.now() } })).data;

export const uploadFacilityDocument = async (facilityId: number, file: File, fields: { kind?: string; title?: string; note?: string; periodId?: number }) => {
  const form = new FormData();
  form.append('file', file);
  if (fields.kind) form.append('kind', fields.kind);
  if (fields.title) form.append('title', fields.title);
  if (fields.note) form.append('note', fields.note);
  if (fields.periodId) form.append('periodId', String(fields.periodId));
  return (await axios.post<{ id: number; kind: string; fileName: string; contentHash: string; byteSize: number }>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};

export const withdrawFacilityDocument = async (facilityId: number, documentId: number, reason: string) =>
  (await axios.post(ABF_API_ROOT + '/facilities/' + facilityId + '/documents/' + documentId + '/withdraw', { reason })).data;

/** Downloads through axios so the bearer token travels with the request; the browser then saves the blob. */
export const downloadFile = async (url: string, fileName: string): Promise<void> => {
  const response = await axios.get(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl; anchor.download = fileName; anchor.click();
  URL.revokeObjectURL(objectUrl);
};

export const getAccountsBoard = async (): Promise<AccountsBoard> =>
  (await axios.get<AccountsBoard>(ABF_API_ROOT + '/accounts/board', { params: { _: Date.now() } })).data;

// F-MK-075: tape-driven surveillance — the latest released tape, its triggers, roll rates between the two latest releases.
export const getTapeSurveillance = async (facilityId: number): Promise<TapeSurveillance> =>
  (await axios.get<TapeSurveillance>(ABF_API_ROOT + '/facilities/' + facilityId + '/tape-surveillance', { params: { _: Date.now() } })).data;
// Item G (V697): the curated obligor master. Merges and aliases are maker-checker.
export const getObligorMasters = async (q?: string): Promise<ObligorMasterList> =>
  (await axios.get<ObligorMasterList>(ABF_API_ROOT + '/obligors', { params: { q: q || undefined, _: Date.now() } })).data;
export const proposeObligorMerge = async (sourceMasterId: number, targetMasterId: number, reason: string): Promise<ObligorMergeRequest> =>
  (await axios.post<ObligorMergeRequest>(ABF_API_ROOT + '/obligors/merges', { sourceMasterId, targetMasterId, reason })).data;
export const reviewObligorMerge = async (requestId: number, approve: boolean, notes: string): Promise<ObligorMergeRequest> =>
  (await axios.post<ObligorMergeRequest>(ABF_API_ROOT + '/obligors/merges/' + requestId + (approve ? '/approve' : '/reject'), { notes })).data;
export const proposeObligorAlias = async (masterId: number, alias: string): Promise<ObligorAlias> =>
  (await axios.post<ObligorAlias>(ABF_API_ROOT + '/obligors/' + masterId + '/aliases', { alias })).data;
export const reviewObligorAlias = async (aliasId: number, approve: boolean, notes: string): Promise<ObligorAlias> =>
  (await axios.post<ObligorAlias>(ABF_API_ROOT + '/obligors/aliases/' + aliasId + (approve ? '/approve' : '/reject'), { notes })).data;

// WS2: pool statistics series and stratification (written at tape release).
export const getPoolSeries = async (facilityId: number, from?: string, to?: string): Promise<PoolSeries> =>
  (await axios.get<PoolSeries>(ABF_API_ROOT + '/facilities/' + facilityId + '/pool-series',
    { params: { from, to, _: Date.now() } })).data;

export const getStrats = async (facilityId: number, tapeIngestId?: number, dimension?: StratDimension): Promise<StratResponse> =>
  (await axios.get<StratResponse>(ABF_API_ROOT + '/facilities/' + facilityId + '/strats',
    { params: { tapeIngestId, dimension, _: Date.now() } })).data;

export const backfillPoolSeries = async (facilityId?: number): Promise<{ tapes: number; written: number; failed: number; results: Array<Record<string, unknown>> }> =>
  (await axios.post(ABF_API_ROOT + '/admin/pool-series/backfill', null, { params: { facilityId } })).data;

const ruleWorkbenchRoot = (facilityId: number) =>
  ABF_API_ROOT + '/facilities/' + facilityId + '/collateral-explorer';

export const getRuleWorkbench = async (
  facilityId: number,
  tapeIngestId: number,
): Promise<RuleWorkbenchReadModel> => {
  const response = await axios.get<RuleWorkbenchReadModel>(
    ruleWorkbenchRoot(facilityId) + '/rule-workbench',
    { params: { tapeIngestId, _: Date.now() } },
  );
  return response.data;
};

export const createRuleSetDraft = async (
  facilityId: number,
  effectiveFrom: string,
): Promise<EligibilityRuleSet> => {
  const response = await axios.post<EligibilityRuleSet>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/drafts',
    { effectiveFrom },
  );
  return response.data;
};

export const updateDraftEligibilityRule = async (
  facilityId: number,
  ruleSetId: number,
  ruleId: number,
  request: EligibilityRuleUpdate,
): Promise<EligibilityRuleSet> => {
  const response = await axios.put<EligibilityRuleSet>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/rules/' + ruleId,
    request,
  );
  return response.data;
};

/** FR-6.16: typed add. */
export const addDraftEligibilityRule = async (
  facilityId: number,
  ruleSetId: number,
  request: EligibilityRuleUpdate,
): Promise<EligibilityRuleSet> => {
  const response = await axios.post<EligibilityRuleSet>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/rules',
    request,
  );
  return response.data;
};

/** FR-6.16: typed remove; the response carries the removed record. */
export const removeDraftEligibilityRule = async (
  facilityId: number,
  ruleSetId: number,
  ruleId: number,
  reason: string,
): Promise<EligibilityRuleSet & { removedRule?: Record<string, unknown> }> => {
  const response = await axios.post<EligibilityRuleSet & { removedRule?: Record<string, unknown> }>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/rules/' + ruleId + '/remove',
    { reason },
  );
  return response.data;
};

export const proposeEligibilityRuleSet = async (
  facilityId: number,
  ruleSetId: number,
  reason: string,
): Promise<EligibilityRuleSet> => {
  const response = await axios.post<EligibilityRuleSet>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/propose',
    { reason },
  );
  return response.data;
};

export const simulateEligibilityRuleSet = async (
  facilityId: number,
  ruleSetId: number,
  tapeIngestId: number,
): Promise<RuleSimulationRun> => {
  const response = await axios.post<RuleSimulationRun>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/simulate',
    { tapeIngestId },
  );
  return response.data;
};

export const reviewEligibilityRuleSet = async (
  facilityId: number,
  ruleSetId: number,
  approve: boolean,
  notes: string,
): Promise<EligibilityRuleSet> => {
  const response = await axios.post<EligibilityRuleSet>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/review',
    { approve, notes },
  );
  return response.data;
};

export const activateEligibilityRuleSet = async (
  facilityId: number,
  ruleSetId: number,
  notes: string,
): Promise<EligibilityRuleSet & { downstreamImpact?: string; scheduledFor?: string }> => {
  const response = await axios.post<EligibilityRuleSet & { downstreamImpact?: string; scheduledFor?: string }>(
    ruleWorkbenchRoot(facilityId) + '/rule-sets/' + ruleSetId + '/activate',
    { notes },
  );
  return response.data;
};

export const getBorrowingBaseWorkspace = async (
  facilityId: number,
): Promise<BorrowingBaseWorkspaceReadModel> => {
  const response = await axios.get<BorrowingBaseWorkspaceReadModel>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/borrowing-base/workspace',
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const recomputeBorrowingBase = async (
  facilityId: number,
  tapeIngestId: number,
  asOfDate: string,
): Promise<BorrowingBaseRun> => {
  const response = await axios.post<BorrowingBaseRun>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/borrowing-base/runs',
    { tapeIngestId, asOfDate, useActiveFacilityVersion: true },
  );
  return response.data;
};

export const compareBorrowingBaseToBbc = async (
  facilityId: number,
  request: BbcComparisonRequest,
): Promise<Record<string, unknown>> => {
  const response = await axios.post<Record<string, unknown>>(
    ABF_API_ROOT + '/round2/facilities/' + facilityId + '/bbc-comparisons',
    request,
  );
  return response.data;
};

export const decideBorrowingBaseRun = async (
  facilityId: number,
  runId: number,
  decision: 'APPROVE' | 'REJECT',
  notes: string,
): Promise<BorrowingBaseRun> => {
  const response = await axios.post<BorrowingBaseRun>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/borrowing-base/runs/' + runId + '/decision',
    { decision, notes },
  );
  return response.data;
};

export const finalizeBorrowingBaseEvidencePack = async (
  facilityId: number,
  runId: number,
  asOfDate: string,
): Promise<Record<string, unknown>> => {
  const response = await axios.post<Record<string, unknown>>(
    ABF_API_ROOT + '/round2/facilities/' + facilityId + '/evidence-packs',
    { packType: 'BORROWING_BASE_AUTHORITY', asOfDate, borrowingBaseRunId: runId },
  );
  return response.data;
};

// WS3: facility holders (the funds that hold a facility and their fixed shares).
export const getFacilityHolders = async (facilityId: number): Promise<FacilityHolders> =>
  (await axios.get<FacilityHolders>(ABF_API_ROOT + '/facilities/' + facilityId + '/holders', { params: { _: Date.now() } })).data;

export interface LenderShareCandidates {
  facilityId: number;
  matched: Array<{ lenderKey: string; lenderName: string; classKey: string; commitmentAmount: number; agreementSharePct: number; holderSharePct: number | null; sourceClause: string | null; versionNo: number; portfolioId: number; portfolioName: string }>;
  unmatched: Array<{ lenderKey: string; lenderName: string; classKey: string; commitmentAmount: number; agreementSharePct: number; sourceClause: string | null; versionNo: number }>;
  ourCommitment: number;
  adoptable: boolean;
  note: string;
}

export const getLenderShareCandidates = async (facilityId: number): Promise<LenderShareCandidates> =>
  (await axios.get<LenderShareCandidates>(ABF_API_ROOT + '/facilities/' + facilityId + '/holders/lender-share-candidates', { params: { _: Date.now() } })).data;

export const adoptLenderSharesAsHolders = async (facilityId: number, effectiveFrom?: string): Promise<FacilityHolders> =>
  (await axios.post<FacilityHolders>(ABF_API_ROOT + '/facilities/' + facilityId + '/holders/adopt-lender-shares', { effectiveFrom })).data;

export const replaceFacilityHolders = async (
  facilityId: number,
  body: { effectiveFrom?: string; source?: string; sourceRef?: string; holders: Array<{ portfolioId: number; sharePct: number; commitmentShare?: number | null }> },
): Promise<FacilityHolders> =>
  (await axios.put<FacilityHolders>(ABF_API_ROOT + '/facilities/' + facilityId + '/holders', body)).data;

export const getFundingWorkspace = async (
  facilityId: number,
  requestId?: number,
): Promise<FundingWorkspaceReadModel> => {
  const response = await axios.get<FundingWorkspaceReadModel>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/funding/workspace',
    { params: { requestId, _: Date.now() } },
  );
  return response.data;
};

export const previewFundingGates = async (
  facilityId: number,
  params: { requestType: 'STANDARD' | 'REPAYMENT'; repaymentKind?: RepaymentKind; amount?: number; valueDate?: string },
): Promise<FundingGateEvaluation & { policy: FundingPolicy }> => {
  const response = await axios.get<FundingGateEvaluation & { policy: FundingPolicy }>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/gates',
    { params: { ...params, _: Date.now() } },
  );
  return response.data;
};

export const createControlledDrawRequest = async (
  facilityId: number,
  request: CreateDrawRequest,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests',
    request,
  );
  return response.data;
};

export const createRepaymentRequest = async (
  facilityId: number,
  request: CreateRepaymentRequest,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/repayment-requests',
    request,
  );
  return response.data;
};

export const decideControlledDrawRequest = async (
  facilityId: number,
  requestId: number,
  decision: 'APPROVE' | 'REJECT',
  notes: string,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/' + requestId + '/decision',
    { decision, notes },
  );
  return response.data;
};

// Fix list 2: the borrower's disbursement account, registered once under maker-checker.
export interface DisbursementAccountRow {
  id: number; beneficiary_name: string; bank_name: string; routing_number: string; account_last4: string; currency?: string | null;
  source_clause?: string | null; verification_method: string; verified_with: string; verified_at: string; verification_note?: string | null;
  state: string; proposed_by: string; proposed_at: string; decided_by?: string | null; decided_at?: string | null; decision_note?: string | null;
  effective_from?: string | null; actorMayApprove?: boolean; gateReason?: string;
}
export interface DisbursementAccountView { facilityId: number; active: DisbursementAccountRow | null; proposed: DisbursementAccountRow | null; history: DisbursementAccountRow[] }
export interface DisbursementAccountRequest {
  beneficiaryName: string; bankName: string; routingNumber: string; accountLast4: string; sourceClause?: string;
  verificationMethod: 'CALL_BACK' | 'SIGNED_LETTER' | 'BANK_CONFIRMATION'; verifiedWith: string; verificationNote?: string;
}
export const getDisbursementAccount = async (facilityId: number): Promise<DisbursementAccountView> =>
  (await axios.get<DisbursementAccountView>(`${ABF_API_ROOT}/facilities/${facilityId}/disbursement-account`, { params: { _: Date.now() } })).data;
export const proposeDisbursementAccount = async (facilityId: number, body: DisbursementAccountRequest): Promise<DisbursementAccountView> =>
  (await axios.post<DisbursementAccountView>(`${ABF_API_ROOT}/facilities/${facilityId}/disbursement-account`, body)).data;
export const approveDisbursementAccount = async (facilityId: number, note?: string): Promise<DisbursementAccountView> =>
  (await axios.post<DisbursementAccountView>(`${ABF_API_ROOT}/facilities/${facilityId}/disbursement-account/approve`, { note })).data;
export const rejectDisbursementAccount = async (facilityId: number, note: string): Promise<DisbursementAccountView> =>
  (await axios.post<DisbursementAccountView>(`${ABF_API_ROOT}/facilities/${facilityId}/disbursement-account/reject`, { note })).data;

// Fix list 3: a holder share change is proposed with the transfers it implies and approved by a checker.
export interface HolderChangeTransfer { portfolioId: number; portfolioCode: string; newSharePct: number; heldOnDate: number; entitled: number; transfer: number }
export interface HolderChangePending {
  id: number; effectiveFrom: string; holders: Array<{ portfolioId: number; sharePct: number }>; sourceRef?: string | null; note?: string | null;
  proposedBy: string; proposedAt: string; actorMayApprove: boolean; gateReason: string;
  preview: { effectiveFrom: string; drawnOnDate: number; markPct: number; transfers: HolderChangeTransfer[]; note: string };
}
export const getPendingHolderChange = async (facilityId: number): Promise<HolderChangePending | null> => {
  const data = (await axios.get<HolderChangePending | Record<string, never>>(`${ABF_API_ROOT}/facilities/${facilityId}/holders/changes/pending`, { params: { _: Date.now() } })).data;
  return data && 'id' in data ? data as HolderChangePending : null;
};
export const proposeHolderChange = async (
  facilityId: number,
  body: { effectiveFrom?: string; sourceRef?: string; note?: string; holders: Array<{ portfolioId: number; sharePct: number }> },
): Promise<HolderChangePending> =>
  (await axios.post<HolderChangePending>(`${ABF_API_ROOT}/facilities/${facilityId}/holders/changes`, body)).data;
export const approveHolderChange = async (facilityId: number, note?: string): Promise<FacilityHolders> =>
  (await axios.post<FacilityHolders>(`${ABF_API_ROOT}/facilities/${facilityId}/holders/changes/approve`, { note })).data;
export const rejectHolderChange = async (facilityId: number, note: string): Promise<FacilityHolders> =>
  (await axios.post<FacilityHolders>(`${ABF_API_ROOT}/facilities/${facilityId}/holders/changes/reject`, { note })).data;

export const releaseControlledDrawRequest = async (
  facilityId: number,
  requestId: number,
  request: ReleaseDrawRequest,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/' + requestId + '/release',
    request,
  );
  return response.data;
};

export const confirmFundingRequest = async (
  facilityId: number,
  requestId: number,
  request: ConfirmFundingRequest,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/' + requestId + '/confirm',
    request,
  );
  return response.data;
};

export const retryFundingPosting = async (
  facilityId: number,
  requestId: number,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/' + requestId + '/retry-posting',
    {},
  );
  return response.data;
};

export const cancelFundingRequest = async (
  facilityId: number,
  requestId: number,
  reason: string,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/' + requestId + '/cancel',
    { reason },
  );
  return response.data;
};

export const proposeFundingReversal = async (
  facilityId: number,
  requestId: number,
  request: ReversalProposal,
): Promise<DrawRequestRecord> => {
  const response = await axios.post<DrawRequestRecord>(
    ABF_API_ROOT + '/facilities/' + facilityId + '/draw-requests/' + requestId + '/reversal',
    request,
  );
  return response.data;
};

export const getMonitoringWorkspace = async (
  facilityId: number,
): Promise<MonitoringWorkspaceReadModel> => {
  const response = await axios.get<MonitoringWorkspaceReadModel>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/analytics`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const getContractOperations = async (
  facilityId: number,
): Promise<ContractOperationsReadModel> => {
  const response = await axios.get<ContractOperationsReadModel>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/operations`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

// Collections close: complete an extracted contract account whose number the
// executed copy does not show. Only the masked number is stored.
/** FR-1.4 — complete or correct the version's payment calendar in review. */
export const completePaymentCalendar = async (
  versionId: number,
  calendar: {
    collection_period: string;
    determination_day: number;
    payment_day: number;
    business_day_convention: string;
    holiday_calendar?: string;
    first_period_start?: string | null;
    source_clause: string;
  },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/payment-calendar`,
    calendar,
  );
  return response.data;
};

export const completeContractAccount = async (
  versionId: number,
  body: { accountKey: string; accountNumber: string; sourceReference: string },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/contract-accounts/complete`,
    body,
  );
  return response.data;
};

export const getActivationReadiness = async (versionId: number): Promise<ActivationReadiness> => {
  const response = await axios.get<ActivationReadiness>(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/readiness`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const decideContractTerm = async (
  versionId: number,
  termKey: string,
  body: CitationInput & { state: 'VERIFIED' | 'NOT_STATED' | 'REJECTED'; verifiedValue?: unknown },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/terms/${encodeURIComponent(termKey)}/decision`,
    body,
  );
  return response.data;
};

/** F-MK-001: the booking funds and shares of a facility version, chosen in contract review. */
export const decideContractHolders = async (
  versionId: number,
  holders: { portfolioId: number; sharePct: number }[],
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/holders`,
    { holders },
  );
  return response.data;
};

export const decideEligibilityDefinition = async (
  versionId: number,
  ruleId: number,
  body: CitationInput & { approve: boolean; legalCriterion?: boolean },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/eligibility-rules/${ruleId}/decision`,
    body,
  );
  return response.data;
};

export const decideCovenantDefinition = async (
  versionId: number,
  covenantId: number,
  body: CitationInput & { approve: boolean },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/covenants/${covenantId}/decision`,
    body,
  );
  return response.data;
};

export const satisfyActivationCondition = async (
  versionId: number,
  conditionId: number,
  evidence: Record<string, unknown>,
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/conditions-precedent/${conditionId}/satisfy`,
    { evidence },
  );
  return response.data;
};

/**
 * The agreement PDF behind a version, as a blob for an inline viewer. F-MK-084: with a job id, the
 * document a row of the version was read from (an inherited term opens the original agreement, not
 * the amendment); the server serves it only when the version or its facility cites that document.
 */
export const getContractSourceDocument = async (versionId: number, jobId?: string | null): Promise<Blob> => {
  const response = await axios.get(
    `${ABF_API_ROOT}/round2/facility-versions/${versionId}/source-document`,
    { responseType: 'blob', params: jobId ? { jobId } : undefined },
  );
  return response.data as Blob;
};

export const getClosingAdvance = async (
  facilityId: number,
  requestId: number,
): Promise<ClosingAdvanceReadiness> => {
  const response = await axios.get<ClosingAdvanceReadiness>(
    `${ABF_API_ROOT}/facilities/${facilityId}/draw-requests/${requestId}/closing-advance`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const updateClosingAdvance = async (
  facilityId: number,
  requestId: number,
  body: {
    amount?: number | null;
    closingDate?: string | null;
    settlementMode?: string | null;
    fundingBasis?: string | null;
    fundingBasisReference?: string | null;
    fundedOn?: string | null;
    evidence?: Record<string, unknown>;
    notes?: string | null;
  },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/facilities/${facilityId}/draw-requests/${requestId}/closing-advance`,
    body,
  );
  return response.data;
};

export const decideClosingAdvance = async (
  facilityId: number,
  requestId: number,
  body: { decision: 'APPROVE' | 'REJECT'; notes?: string },
) => {
  const response = await axios.post(
    `${ABF_API_ROOT}/facilities/${facilityId}/draw-requests/${requestId}/closing-advance/decision`,
    body,
  );
  return response.data;
};

export const getFacilityReconciliation = async (
  facilityId: number,
  tapeIngestId?: number | null,
): Promise<ReconciliationReadModel> => {
  const response = await axios.get<ReconciliationReadModel>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/reconciliation`,
    { params: { _: Date.now(), ...(tapeIngestId ? { tapeIngestId } : {}) } },
  );
  return response.data;
};

// Released servicer tapes for a facility, latest first. Used by the Monitoring
// cash/remittance intake to bind the remittance batch and cash-control run to
// the governing released tape (the same tape id the drawer resolved as a prop).
export const getFacilityTapes = async (
  facilityId: number,
): Promise<Array<Record<string, any>>> => {
  const response = await axios.get<Array<Record<string, any>>>(
    `${ABF_API_ROOT}/facilities/${facilityId}/tapes`,
    { params: { _: Date.now() } },
  );
  return Array.isArray(response.data) ? response.data : [];
};

export const getFacilityWaterfallRuns = async (
  facilityId: number,
): Promise<WaterfallRun[]> => {
  const response = await axios.get<WaterfallRun[]>(
    `${ABF_API_ROOT}/facilities/${facilityId}/waterfall/runs`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const runAndFinalizeFacilityWaterfall = async (
  facilityId: number,
  asOfDate: string,
  cashTransactionIds: number[],
): Promise<WaterfallRun> => {
  const response = await axios.post<WaterfallRun>(
    `${ABF_API_ROOT}/facilities/${facilityId}/waterfall/run-decision`,
    {
      asOfDate,
      periodLabel: asOfDate.slice(0, 7),
      cashTransactionIds,
    },
  );
  return response.data;
};

export const calculateFacilityCovenants = async (
  facilityId: number,
  testDate: string,
): Promise<CovenantCalculationResult> => {
  const response = await axios.post<CovenantCalculationResult>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/covenants/runs`,
    { testDate, sourceReference: 'Facility Monitoring workspace' },
  );
  return response.data;
};

// F-NS-013: exception disposition brought into the workspace (previously only in
// the compatibility Control Room). Maker proposes a governed resolution; a
// distinct checker approves or rejects. Wired to the existing Round-2 endpoints.
export const classifyFacilityException = async (
  facilityId: number,
  exceptionId: number,
  body: { classification: string; resolution: string; notes: string },
): Promise<Record<string, any>> => {
  const response = await axios.post<Record<string, any>>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/exceptions/${exceptionId}/classify`,
    body,
  );
  return response.data;
};

export const reviewFacilityException = async (
  facilityId: number,
  exceptionId: number,
  body: { approve: boolean; notes: string },
): Promise<Record<string, any>> => {
  const response = await axios.post<Record<string, any>>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/exceptions/${exceptionId}/review`,
    body,
  );
  return response.data;
};

export const requestFacilityCovenantWaiver = async (
  facilityId: number,
  run: CovenantRun,
  notes: string,
): Promise<CovenantWaiver> => {
  const response = await axios.post<CovenantWaiver>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/covenants/waivers`,
    {
      covenantDefinitionId: run.covenant_definition_id,
      covenantRunId: run.id,
      effectiveFrom: run.test_date,
      waiverType: 'TEMPORARY',
      conditions: {
        requestedFrom: 'Facility Monitoring workspace',
        covenantKey: run.covenant_key,
        actualValue: run.actual_value,
        thresholdValue: run.threshold_value,
      },
      sourceClause: run.source_clause,
      notes: notes.trim(),
    },
  );
  return response.data;
};

export const reviewFacilityCovenantWaiver = async (
  facilityId: number,
  waiverId: number,
  approve: boolean,
  notes: string,
): Promise<CovenantWaiver> => {
  const response = await axios.post<CovenantWaiver>(
    `${ABF_API_ROOT}/round2/facilities/${facilityId}/covenants/waivers/${waiverId}/review`,
    { approve, notes: notes.trim() },
  );
  return response.data;
};

export const refreshMonitoringEvidence = async (
  facilityId: number,
  asOfDate: string,
): Promise<Record<string, unknown>> => {
  const response = await axios.post<Record<string, unknown>>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/monitoring-runs`,
    {
      asOfDate,
      owner: 'ABF Operations',
      thesis: 'Released collateral and official exposure monitoring',
      recommendedAction: 'Investigate only material exceptions before the next facility decision.',
      dueDate: asOfDate,
    },
  );
  return response.data;
};

export const runFacilityScenario = async (
  facilityId: number,
  scenarioDefinitionId: number,
  asOfDate: string,
): Promise<ScenarioRun> => {
  const response = await axios.post<ScenarioRun>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/scenario-runs`,
    { scenarioDefinitionId, asOfDate },
  );
  return response.data;
};

export const createTenantScenarioDefinition = async (request: {
  scenarioCode: string;
  scenarioName: string;
  severity: string;
  assumptions: {
    cdrPct: number;
    lossSeverityPct: number;
    eligibilityHaircutPct: number;
  };
}): Promise<ScenarioDefinition> => {
  const response = await axios.post<ScenarioDefinition>(
    `${ABF_API_ROOT}/round3/scenario-definitions`,
    request,
  );
  return response.data;
};

export const createFacilityPriceEvidence = async (
  facilityId: number,
  request: {
    priceDate: string;
    markPct: number;
    evidenceType: string;
    providerIdentifier: string;
    dataQuality: string;
    sourceReference: string;
    notes: string;
  },
): Promise<ValuationPriceEvidence> => {
  const response = await axios.post<ValuationPriceEvidence>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/price-evidence`,
    request,
  );
  return response.data;
};

export const calculateFacilityValuation = async (
  facilityId: number,
  request: {
    effectiveDate: string;
    priceEvidenceId: number;
    proposedMarkPct: number;
    rationale: string;
    scenarioRunId?: number | null;
  },
): Promise<ValuationRun> => {
  const response = await axios.post<ValuationRun>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/valuation-runs`,
    { ...request, methodology: 'GOVERNED_PRICE_EVIDENCE' },
  );
  return response.data;
};

export const decideFacilityValuation = async (
  facilityId: number,
  valuationId: number,
  decision: 'APPROVE' | 'CHALLENGE',
  notes: string,
): Promise<ValuationRun> => {
  const path = decision === 'APPROVE'
    ? `${ABF_API_ROOT}/round3/facilities/${facilityId}/valuation-runs/${valuationId}/approve-decision`
    : `${ABF_API_ROOT}/round3/facilities/${facilityId}/valuation-runs/${valuationId}/review`;
  const response = await axios.post<ValuationRun>(path, {
    decision: decision === 'APPROVE' ? 'REVIEW' : 'CHALLENGE',
    notes,
  });
  return response.data;
};

export const getReportingWorkspace = async (
  facilityId: number,
): Promise<ReportingWorkspaceReadModel> => {
  const response = await axios.get<ReportingWorkspaceReadModel>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/distribution`,
    { params: { _: Date.now() } },
  );
  return response.data;
};

export const createFacilityReport = async (
  facilityId: number,
  asOfDate: string,
): Promise<ReportInstance> => {
  const response = await axios.post<ReportInstance>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/reports`,
    { asOfDate, reportType: 'ABF_FACILITY_CONTROL', recipients: [] },
  );
  return response.data;
};

export const certifyFacilityReport = async (
  facilityId: number,
  reportId: number,
  notes: string,
): Promise<ReportInstance> => {
  const response = await axios.post<ReportInstance>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/reports/${reportId}/certification-decision`,
    { notes },
  );
  return response.data;
};

export const deliverFacilityReport = async (
  facilityId: number,
  reportId: number,
  recipient: string,
): Promise<ReportInstance> => {
  const response = await axios.post<ReportInstance>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/reports/${reportId}/deliver`,
    {
      channel: 'CONTROLLED_EXPORT',
      deliveryReference: `facility-report-${reportId}`,
      recipients: [recipient.trim()],
    },
  );
  return response.data;
};

export const restateFacilityReport = async (
  facilityId: number,
  reportId: number,
  asOfDate: string,
  reason: string,
  recipient?: string,
): Promise<ReportInstance> => {
  const response = await axios.post<ReportInstance>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/reports/${reportId}/restate`,
    {
      asOfDate,
      reportType: 'ABF_FACILITY_CONTROL',
      restatementReason: reason,
      recipients: recipient?.trim() ? [recipient.trim()] : [],
    },
  );
  return response.data;
};

export const publishFacilityHandoff = async (
  facilityId: number,
  reportId: number,
  destinationName: string,
): Promise<OutboundExchange> => {
  const response = await axios.post<OutboundExchange>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/reports/${reportId}/accounting-handoffs`,
    { destinationType: 'FUND_ADMINISTRATOR', destinationName: destinationName.trim() },
  );
  return response.data;
};

export const acknowledgeFacilityHandoff = async (
  facilityId: number,
  exchangeId: number,
): Promise<OutboundExchange> => {
  const response = await axios.post<OutboundExchange>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/accounting-handoffs/${exchangeId}/acknowledge`,
    {
      accepted: true,
      acknowledgementReference: `ACK-${exchangeId}`,
      details: { reconciled: true },
    },
  );
  return response.data;
};

export const rejectFacilityHandoff = async (
  facilityId: number,
  exchangeId: number,
  notes: string,
): Promise<OutboundExchange> => {
  const response = await axios.post<OutboundExchange>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/accounting-handoffs/${exchangeId}/acknowledge`,
    {
      accepted: false,
      acknowledgementReference: `REJECT-${exchangeId}`,
      errorMessage: notes.trim(),
      details: { deliveryAccepted: false, reviewNotes: notes.trim() },
    },
  );
  return response.data;
};

export const replayFacilityHandoff = async (
  facilityId: number,
  exchangeId: number,
): Promise<OutboundExchange> => {
  const response = await axios.post<OutboundExchange>(
    `${ABF_API_ROOT}/round3/facilities/${facilityId}/accounting-handoffs/${exchangeId}/replay`,
  );
  return response.data;
};

/**
 * What to tell the operator when a call fails.
 *
 * <p>The server's own message always wins: on these screens a refusal is a
 * written sentence naming the record and, where a date is wrong, a date that
 * would work. Everything below is for the cases where there is no such message —
 * and in those cases the operator must not be handed the HTTP client's internals.
 * "Request failed with status code 500" was reaching the screen because
 * {@code error.message} was preferred over the caller's fallback.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return error instanceof Error ? error.message : fallback;

  const payload = error.response?.data as { error?: string; message?: string; details?: string }
    | undefined;
  const fromServer = payload?.error || payload?.message;
  if (fromServer) return fromServer;

  // No response at all: the server is down, restarting, or unreachable. Nothing
  // was sent, and saying so is the only useful thing to say.
  if (!error.response) {
    return 'The server did not respond, so nothing was sent. It may be restarting — try again in a moment.';
  }

  const status = error.response.status;
  if (status === 401) return 'Your session has expired. Sign in again and retry.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That record no longer exists. Reload the page.';
  if (status >= 500) {
    return `${fallback}. The server failed to handle the request and nothing was changed`
      + (payload?.details ? ` — ${payload.details}` : '.');
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// Slice 7 — period reports, renderings, evidence packs, recipients, deliveries,
// hand-offs and acknowledgements (ABFReportingController, /api/credit/abf/reporting).
// ---------------------------------------------------------------------------


const REPORTING_ROOT = `${ABF_API_ROOT}/reporting`;

export const getPeriodReportingWorkspace = async (facilityId: number): Promise<ReportingWorkspaceModel> =>
  (await axios.get<ReportingWorkspaceModel>(`${REPORTING_ROOT}/facilities/${facilityId}/workspace`, { params: { _: Date.now() } })).data;

export const getPeriodReportReadiness = async (facilityId: number, periodId: number): Promise<ReportReadiness> =>
  (await axios.get<ReportReadiness>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/readiness`)).data;

export const updateReportingPolicy = async (facilityId: number, policy: Partial<ReportingPolicy>): Promise<ReportingPolicy> =>
  (await axios.put<ReportingPolicy>(`${REPORTING_ROOT}/facilities/${facilityId}/policy`, policy)).data;

export const assemblePeriodReport = async (facilityId: number, periodId: number): Promise<ReportDetail> =>
  (await axios.post<ReportDetail>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/reports`)).data;

export const assembleQuarterReport = async (facilityId: number, label: string): Promise<ReportDetail> =>
  (await axios.post<ReportDetail>(`${REPORTING_ROOT}/facilities/${facilityId}/quarters/${label}/reports`)).data;

export const getPeriodReport = async (facilityId: number, reportId: number): Promise<ReportDetail> =>
  (await axios.get<ReportDetail>(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}`, { params: { _: Date.now() } })).data;

export const discardPeriodReport = async (facilityId: number, reportId: number, reason: string): Promise<ReportDetail> =>
  (await axios.post<ReportDetail>(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/discard`, { reason })).data;

export const certifyPeriodReport = async (facilityId: number, reportId: number, notes: string): Promise<ReportDetail> =>
  (await axios.post<ReportDetail>(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/certification-decision`, { notes })).data;

export const restatePeriodReport = async (facilityId: number, reportId: number, reason: string): Promise<ReportDetail> =>
  (await axios.post<ReportDetail>(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/restate`, { reason })).data;

export const verifyPeriodReport = async (facilityId: number, reportId: number): Promise<VerifyResult> =>
  (await axios.post<VerifyResult>(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/verify`)).data;

export const reportArtifactPath = (facilityId: number, reportId: number, format: string, download = false) =>
  `${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/artifacts/${format}${download ? '?download=true' : ''}`;

/** Fetch a rendering with the JWT (an <iframe src> or <a href> carries no Authorization header). */
export const fetchReportArtifactBlob = async (facilityId: number, reportId: number, format: string): Promise<Blob> =>
  (await axios.get(reportArtifactPath(facilityId, reportId, format), { responseType: 'blob' })).data as Blob;

export const listEvidencePacks = async (facilityId: number): Promise<EvidencePackRef[]> =>
  (await axios.get<EvidencePackRef[]>(`${REPORTING_ROOT}/facilities/${facilityId}/evidence-packs`)).data;

export const fetchEvidencePackZip = async (facilityId: number, packId: number): Promise<Blob> =>
  (await axios.get(`${REPORTING_ROOT}/facilities/${facilityId}/evidence-packs/${packId}/download`, { responseType: 'blob' })).data as Blob;

export const getRecipientRegistry = async (): Promise<RecipientRegistry> =>
  (await axios.get<RecipientRegistry>(`${REPORTING_ROOT}/recipients`, { params: { _: Date.now() } })).data;

export const proposeRecipient = async (request: RecipientRequest): Promise<Recipient> =>
  (await axios.post<Recipient>(`${REPORTING_ROOT}/recipients`, request)).data;

export const proposeRecipientChange = async (recipientId: number, request: RecipientRequest): Promise<Recipient> =>
  (await axios.put<Recipient>(`${REPORTING_ROOT}/recipients/${recipientId}`, request)).data;

export const proposeRecipientRetire = async (recipientId: number, reason: string): Promise<Recipient> =>
  (await axios.delete<Recipient>(`${REPORTING_ROOT}/recipients/${recipientId}`, { data: { reason } })).data;

export const approveRecipient = async (recipientId: number, notes?: string): Promise<Recipient> =>
  (await axios.post<Recipient>(`${REPORTING_ROOT}/recipients/${recipientId}/approve`, { notes })).data;

export const rejectRecipient = async (recipientId: number, notes: string): Promise<Recipient> =>
  (await axios.post<Recipient>(`${REPORTING_ROOT}/recipients/${recipientId}/reject`, { notes })).data;

export const uploadRecipientRegisterFile = async (file: File): Promise<RegisterFileResult> => {
  const form = new FormData();
  form.append('file', file);
  return (await axios.post<RegisterFileResult>(`${REPORTING_ROOT}/recipients/register-file`, form)).data;
};

export const getFacilityRecipients = async (facilityId: number): Promise<FacilityRecipients> =>
  (await axios.get<FacilityRecipients>(`${REPORTING_ROOT}/facilities/${facilityId}/recipients`, { params: { _: Date.now() } })).data;

export const bindFacilityRecipient = async (facilityId: number, recipientId: number, receives: string, formats?: string[]): Promise<RecipientBinding> =>
  (await axios.post<RecipientBinding>(`${REPORTING_ROOT}/facilities/${facilityId}/recipients`, { recipientId, receives, formats })).data;

export const approveFacilityRecipient = async (facilityId: number, bindingId: number, notes?: string): Promise<RecipientBinding> =>
  (await axios.post<RecipientBinding>(`${REPORTING_ROOT}/facilities/${facilityId}/recipients/${bindingId}/approve`, { notes })).data;

export const unbindFacilityRecipient = async (facilityId: number, bindingId: number, reason: string): Promise<RecipientBinding> =>
  (await axios.delete<RecipientBinding>(`${REPORTING_ROOT}/facilities/${facilityId}/recipients/${bindingId}`, { data: { reason } })).data;

export const queueReportDeliveries = async (facilityId: number, reportId: number, recipientId: number, formats?: string[]): Promise<{ queued: number; deliveries: ReportDelivery[] }> =>
  (await axios.post(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/deliveries`, { recipientId, formats })).data;

export const publishPeriodHandoff = async (facilityId: number, reportId: number, recipientId: number): Promise<ReportHandoff> =>
  (await axios.post<ReportHandoff>(`${REPORTING_ROOT}/facilities/${facilityId}/reports/${reportId}/handoffs`, { recipientId })).data;

export const replayReportDelivery = async (facilityId: number, deliveryId: number): Promise<ReportDelivery> =>
  (await axios.post<ReportDelivery>(`${REPORTING_ROOT}/facilities/${facilityId}/deliveries/${deliveryId}/replay`)).data;

export const replayPeriodHandoff = async (facilityId: number, exchangeId: number): Promise<ReportHandoff> =>
  (await axios.post<ReportHandoff>(`${REPORTING_ROOT}/facilities/${facilityId}/handoffs/${exchangeId}/replay`)).data;

export const recordManualDelivery = async (facilityId: number, deliveryId: number, body: { reference: string; evidenceRef?: string; evidenceFileName?: string; evidenceBase64?: string }): Promise<ReportDelivery> =>
  (await axios.post<ReportDelivery>(`${REPORTING_ROOT}/facilities/${facilityId}/deliveries/${deliveryId}/manual-delivery`, body)).data;

export const acknowledgeReportDelivery = async (facilityId: number, deliveryId: number, body: AbfAcknowledgementRequest): Promise<ReportDelivery> =>
  (await axios.post<ReportDelivery>(`${REPORTING_ROOT}/facilities/${facilityId}/deliveries/${deliveryId}/acknowledgement`, body)).data;

export const acknowledgePeriodHandoff = async (facilityId: number, exchangeId: number, body: AbfAcknowledgementRequest): Promise<ReportHandoff> =>
  (await axios.post<ReportHandoff>(`${REPORTING_ROOT}/facilities/${facilityId}/handoffs/${exchangeId}/acknowledgement`, body)).data;

// ---------------------------------------------------------------- one period, one page (distribution flow)
export const getPeriodDistribution = async (facilityId: number, periodId: number): Promise<DistributionPage> =>
  (await axios.get<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution`, { params: { _: Date.now() } })).data;
export const distributionAssemble = async (facilityId: number, periodId: number): Promise<DistributionPage> =>
  (await axios.post<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/assemble`)).data;
export const distributionCertify = async (facilityId: number, periodId: number, reportId: number, notes: string): Promise<DistributionPage> =>
  (await axios.post<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/certify`, { reportId, notes })).data;
export const distributionRestate = async (facilityId: number, periodId: number, reportId: number, reason: string): Promise<DistributionPage> =>
  (await axios.post<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/restate`, { reportId, reason })).data;
export const distributionCertifyFundReports = async (facilityId: number, periodId: number, expectedReportIds: number[], notes: string): Promise<DistributionPage> =>
  (await axios.post<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/fund-reports/certify`, { expectedReportIds, notes })).data;
export const distributionReissueFundReports = async (facilityId: number, periodId: number): Promise<DistributionPage> =>
  (await axios.post<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/fund-reports/reissue`)).data;
export const distributionAcknowledge = async (facilityId: number, periodId: number, recipientId: number, body: AbfAcknowledgementRequest): Promise<DistributionPage> =>
  (await axios.post<DistributionPage>(`${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/recipients/${recipientId}/acknowledgement`, body)).data;

/** Where a dropped acknowledgement file is (F-MK-067). STILL_READING: the page stopped waiting; the server records it. */
export interface AcknowledgementDrop {
  jobId: string; state: 'PENDING' | 'RECORDED' | 'FAILED' | 'STILL_READING'; message: string; droppedBy: string;
  /** The same file was dropped before; recordedNow says whether this drop is what recorded it. */
  duplicate?: boolean; recordedNow?: boolean;
}

/**
 * The administrator's acknowledgement file, dropped on the period page (F-MK-067). The server sends
 * it to the document pipeline and records the intent with the signed-in user, then approves and
 * records it as that user once it has been read — whether or not this page is still open. The page
 * follows along for up to four minutes; after that it stops waiting and says so, and the server
 * finishes the job.
 */
export const dropAcknowledgementFile = async (facilityId: number, periodId: number, file: File,
  onProgress?: (phase: string) => void): Promise<AcknowledgementDrop> => {
  const form = new FormData();
  form.append('file', file);
  onProgress?.('Reading the file');
  const base = `${REPORTING_ROOT}/facilities/${facilityId}/periods/${periodId}/distribution/acknowledgement-files`;
  let drop = (await axios.post<AcknowledgementDrop>(base, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  const duplicate = !!drop.duplicate;
  const deadline = Date.now() + 4 * 60 * 1000;
  while (drop.state === 'PENDING') {
    if (Date.now() > deadline) return { ...drop, duplicate, state: 'STILL_READING' };
    await new Promise(resolve => setTimeout(resolve, 2500));
    drop = (await axios.post<AcknowledgementDrop>(`${base}/${encodeURIComponent(drop.jobId)}/complete`)).data;
    onProgress?.(drop.message);
  }
  if (drop.state === 'FAILED') throw new Error(drop.message || 'The acknowledgement could not be recorded');
  return { ...drop, duplicate };
};

export const runDeliveryOutboxNow = async (): Promise<unknown> =>
  (await axios.post('/api/admin/scheduled/abf-delivery-outbox', null, { params: { force: 'true' } })).data;

/** Read a File as base64 for the acknowledgement's attached evidence. */
export const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',').pop() || '');
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

// ---------------------------------------------------------------------------
// Slice 8 — portfolio board, work queue, attention items, activity timeline
// (ABFBoardController, /api/credit/abf/board).
// ---------------------------------------------------------------------------

const BOARD_ROOT = `${ABF_API_ROOT}/board`;

export const getPortfolioBoard = async (needsAttention = false, mine = false): Promise<PortfolioBoard> =>
  (await axios.get<PortfolioBoard>(`${BOARD_ROOT}/portfolio`, { params: { needsAttention, mine, _: Date.now() } })).data;

export const getWorkQueue = async (params: { workflow?: string; severity?: string; facilityId?: number; mine?: boolean; includeTriaged?: boolean } = {}): Promise<WorkQueue> =>
  (await axios.get<WorkQueue>(`${BOARD_ROOT}/queue`, { params: { ...params, _: Date.now() } })).data;

export const assignAttentionOwner = async (key: string, owner: string): Promise<unknown> =>
  (await axios.post(`${BOARD_ROOT}/queue/owner`, { key, owner })).data;

export const triageAttentionItems = async (keys: string[], action: 'ACKNOWLEDGE' | 'SNOOZE' | 'DISMISS' | 'REOPEN', reason?: string, snoozeUntil?: string): Promise<TriageResult> =>
  (await axios.post<TriageResult>(`${BOARD_ROOT}/queue/triage`, { keys, action, reason, snoozeUntil })).data;

export const getFacilityAttention = async (facilityId: number): Promise<AttentionItem[]> =>
  (await axios.get<AttentionItem[]>(`${BOARD_ROOT}/facilities/${facilityId}/attention`, { params: { _: Date.now() } })).data;

export const getActivityTimeline = async (facilityId: number, params: { workflow?: string; actor?: string; periodId?: number; limit?: number; offset?: number } = {}): Promise<ActivityTimeline> =>
  (await axios.get<ActivityTimeline>(`${BOARD_ROOT}/facilities/${facilityId}/timeline`, { params: { ...params, _: Date.now() } })).data;

// ---------------------------------------------------------------- WS5: data feed and underwriting case

export interface FeedCatalogue {
  schemaVersion: string;
  datasets: Array<{ name: string; description: string; authority: string; incrementalOnTapeRelease: boolean; columns: Array<{ name: string; type: string; description: string }> }>;
  formats: string[]; cadences: string[]; channels: string[];
}
export interface FeedRun {
  id: number; recipient_id: number; legal_name: string; channel: string; run_kind: string; trigger_kind: string; facility_id: number | null; facility_code: string | null;
  tape_ingest_id: number | null; as_of_date: string; schema_version: string; status: string; datasets_json: string[]; manifest_hash: string | null; row_count: number;
  byte_count: number; file_count: number; transfer_id: string | null; error: string | null; started_by: string; started_at: string; finished_at: string | null; duration_ms: number | null;
  manifest_json?: Record<string, unknown> | null; receipt_json?: Record<string, unknown> | null;
}
export const getFeedCatalogue = async (): Promise<FeedCatalogue> => (await axios.get<FeedCatalogue>(ABF_API_ROOT + '/feed/datasets')).data;
export const getFeedRuns = async (params: { recipientId?: number; facilityId?: number; limit?: number }): Promise<FeedRun[]> =>
  (await axios.get<FeedRun[]>(ABF_API_ROOT + '/feed/runs', { params: { ...params, _: Date.now() } })).data;
export const getFeedRun = async (runId: number): Promise<FeedRun> => (await axios.get<FeedRun>(ABF_API_ROOT + '/feed/runs/' + runId)).data;
export const runFeedNow = async (recipientId: number, body?: { facilityId?: number; asOf?: string }): Promise<FeedRun> =>
  (await axios.post<FeedRun>(ABF_API_ROOT + '/feed/recipients/' + recipientId + '/run', body || {})).data;
export const probeFeedRecipient = async (recipientId: number): Promise<FeedRun> =>
  (await axios.post<FeedRun>(ABF_API_ROOT + '/feed/recipients/' + recipientId + '/probe')).data;
export const feedSchemaPath = ABF_API_ROOT + '/feed/schema';

export interface UnderwritingMetric {
  key: string; label: string; unit: 'PCT' | 'YEARS'; value: number | null; sourceKind: 'AGREEMENT' | 'MAKER' | null; sourceClause: string | null; note: string | null;
  asOfDate: string | null; versionNo: number | null; setBy: string | null; setAt: string | null;
  history: Array<{ versionNo: number; value: number; sourceKind: string; setBy: string; setAt: string; supersededAt: string }>;
}
export interface UnderwritingCase {
  facilityId: number; metrics: UnderwritingMetric[];
  agreementCandidates: Array<{ key: string; label: string; value: number; termKey: string; sourceClause: string; versionNo: number }>;
  authorityBoundary: string;
}
export const getUnderwritingCase = async (facilityId: number): Promise<UnderwritingCase> =>
  (await axios.get<UnderwritingCase>(ABF_API_ROOT + '/facilities/' + facilityId + '/underwriting-case', { params: { _: Date.now() } })).data;
export const putUnderwritingCase = async (facilityId: number, body: { asOf?: string; metrics: Array<{ key: string; value: number | string; sourceKind: string; sourceClause?: string; note?: string }> }): Promise<UnderwritingCase> =>
  (await axios.put<UnderwritingCase>(ABF_API_ROOT + '/facilities/' + facilityId + '/underwriting-case', body)).data;
export const seedUnderwritingCaseFromAgreement = async (facilityId: number): Promise<UnderwritingCase> =>
  (await axios.post<UnderwritingCase>(ABF_API_ROOT + '/facilities/' + facilityId + '/underwriting-case/seed-from-agreement')).data;

// ---------------------------------------------------------------- WS6: fund reports and holder statements

export interface FundSummary { portfolio_id: number; portfolio_name: string; portfolio_code: string | null; facilities_held: number; facilities_ever: number; commitment_held: number; reports: number }
export interface FundReadinessFacility {
  facilityId: number; facilityCode: string; facilityName: string; sharePct: number; periodId: number | null; periodStatus: string | null; periodVersionNo: number | null; signedOff: boolean;
  facilityReportId: number | null; facilityReportVersion: number | null; facilityReportState: string | null; facilityContentHash: string | null; waivedReason: string | null; status: 'READY' | 'WAIVED' | 'BLOCKED';
}
export interface FundReadiness { portfolioId: number; periodLabel: string; periodEnd: string; facilities: FundReadinessFacility[]; blockers: Array<{ facilityId: number; facilityCode?: string; code: string; reason: string }>; ready: boolean; waivedCount: number }
export interface FundReportSummary { id: number; report_version: number; state: string; certification_state: string; certified_by: string | null; certified_at: string | null; content_hash: string; created_by: string; created_at: string }
export interface FundWorkspace {
  portfolio: { id: number; portfolio_name: string; portfolio_code: string | null };
  holdings: Array<{ facility_id: number; facility_code: string; facility_name: string; obligor_name: string; currency: string; facility_status: string; share_pct: number; commitment_share: number | null; effective_from: string; source: string }>;
  months: Array<{ periodLabel: string; readiness: FundReadiness; fundReport: FundReportSummary | null; holderStatement: FundReportSummary | null }>;
  reports: Array<FundReportSummary & { report_kind: string; period_label: string; parent_report_id: number | null; restatementFlags: { status: string; flags: Array<{ facilityId: number; facilityReportId: number; state: string; reason: string }> } }>;
  authorityBoundary: string;
}
export interface FundReportDetail extends Record<string, any> {
  acknowledgementGate?: { mine: boolean; reason: string };
  id: number; report_kind: 'FUND_PERIOD' | 'HOLDER_STATEMENT'; period_label: string; report_version: number; state: string; content_hash: string; certified_by: string | null; certified_at: string | null;
  created_by: string; created_at: string; parent_report_id: number | null; restatement_reason: string | null;
  semantic_payload_json: Record<string, any>; authority_manifest_json: Record<string, any>;
  artifacts: Array<{ id: number; format: string; output_hash: string; byte_size: number; rendered_at: string; last_verify_status: string | null }>;
  deliveries: Array<Record<string, any>>; gate: { actorMayCertify: boolean; reason: string; policy: string; maker: string };
  restatementFlags: { status: string; flags: Array<{ facilityId: number; facilityReportId: number; state: string; reason: string }> };
  components: Array<{ facility_id: number; facility_code: string; facility_report_id: number | null; facility_report_version: number | null; facility_content_hash: string | null; share_pct: number; waived_reason: string | null }>;
  lineage: Array<{ id: number; report_version: number; state: string; certified_by: string | null; certified_at: string | null; parent_report_id: number | null; restatement_reason: string | null; content_hash: string }>;
}
const FUND_ROOT = ABF_API_ROOT + '/reporting/portfolios';
export const getFunds = async (): Promise<FundSummary[]> => (await axios.get<FundSummary[]>(FUND_ROOT, { params: { _: Date.now() } })).data;
export const getFundWorkspace = async (portfolioId: number): Promise<FundWorkspace> => (await axios.get<FundWorkspace>(`${FUND_ROOT}/${portfolioId}/workspace`, { params: { _: Date.now() } })).data;
export const getFundReadiness = async (portfolioId: number, label: string): Promise<FundReadiness> => (await axios.get<FundReadiness>(`${FUND_ROOT}/${portfolioId}/periods/${label}/readiness`)).data;
export const assembleFundReport = async (portfolioId: number, label: string, waivers: Array<{ facilityId: number; reason: string }>): Promise<FundReportDetail> =>
  (await axios.post<FundReportDetail>(`${FUND_ROOT}/${portfolioId}/periods/${label}/fund-reports`, { waivers })).data;
export const assembleHolderStatement = async (portfolioId: number, label: string, waivers: Array<{ facilityId: number; reason: string }>): Promise<FundReportDetail> =>
  (await axios.post<FundReportDetail>(`${FUND_ROOT}/${portfolioId}/periods/${label}/holder-statements`, { waivers })).data;
export const getFundReport = async (portfolioId: number, reportId: number): Promise<FundReportDetail> => (await axios.get<FundReportDetail>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}`, { params: { _: Date.now() } })).data;
export const discardFundReport = async (portfolioId: number, reportId: number, reason: string): Promise<FundReportDetail> => (await axios.post<FundReportDetail>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/discard`, { reason })).data;
export const certifyFundReport = async (portfolioId: number, reportId: number, notes: string): Promise<FundReportDetail> => (await axios.post<FundReportDetail>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/certification-decision`, { notes })).data;
export const restateFundReport = async (portfolioId: number, reportId: number, reason: string): Promise<FundReportDetail> => (await axios.post<FundReportDetail>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/restate`, { reason })).data;
export const verifyFundReport = async (portfolioId: number, reportId: number): Promise<{ status: string; artifacts: Array<{ format: string; status: string }>; componentsStillCertified: Array<{ facilityId: number; reportId: number; state: string; hashMatch: boolean }> }> =>
  (await axios.post(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/verify`)).data;
export const fundReportArtifactPath = (portfolioId: number, reportId: number, format: string, download = false) => `${FUND_ROOT}/${portfolioId}/reports/${reportId}/artifacts/${format}${download ? '?download=true' : ''}`;
export const fetchFundReportArtifactBlob = async (portfolioId: number, reportId: number, format: string): Promise<Blob> =>
  (await axios.get(fundReportArtifactPath(portfolioId, reportId, format, true), { responseType: 'blob' })).data as Blob;
export const fetchFundReportPreviewBlob = async (portfolioId: number, reportId: number): Promise<Blob> =>
  (await axios.get(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/preview`, { responseType: 'blob' })).data as Blob;

export const recordFundPortalDelivery = async (portfolioId: number, reportId: number, deliveryId: number, body: { reference: string; evidenceFileName?: string; evidenceBase64?: string }): Promise<ReportDelivery> =>
  (await axios.post<ReportDelivery>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/deliveries/${deliveryId}/manual-delivery`, body)).data;
export const acknowledgeFundDelivery = async (portfolioId: number, reportId: number, deliveryId: number, body: AbfAcknowledgementRequest): Promise<ReportDelivery> =>
  (await axios.post<ReportDelivery>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/deliveries/${deliveryId}/acknowledgement`, body)).data;
export const replayFundDelivery = async (portfolioId: number, reportId: number, deliveryId: number): Promise<ReportDelivery> =>
  (await axios.post<ReportDelivery>(`${FUND_ROOT}/${portfolioId}/reports/${reportId}/deliveries/${deliveryId}/replay`)).data;

// ---------------------------------------------------------------- WS7 D-9: accrual basis (contract term)
export interface AccrualBasis {
  facilityId: number; activeVersionId: number | null; activeVersionNo: number | null; basis: string; sourceClause: string | null; bases: string[];
  facilityRatePct: number | null; wacPct: number | null; wacTapeIngestId: number | null; rateInForcePct: number | null; note: string;
  recentAccruals: Array<{ accrual_date: string; drawn_balance: number; all_in_rate_pct: number; accrual_basis: string; facility_rate_pct: number | null; wac_pct: number | null; wac_tape_ingest_id: number | null; interest_accrual: number; total_accrual: number; source_ref: string }>;
  authorityBoundary: string;
}
/** Work plan item F: the facility phase and the governed revolver term-out. */
export interface TermOutRequestRow {
  id: number; facilityId: number; phase: string; effectiveFrom: string; cause: string;
  status: 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED' | 'LIFTED' | string;
  notes?: string | null; requestedBy?: string | null; requestedAt?: string | null;
  decidedBy?: string | null; decidedAt?: string | null; decisionNotes?: string | null; sourceRef?: string;
}
export const getFacilityPhase = async (facilityId: number): Promise<import('../types/facilityWorkspace').FacilityPhase> =>
  (await axios.get(ABF_API_ROOT + '/facilities/' + facilityId + '/structure/phase', { params: { _: Date.now() } })).data;
export const getTermOutRequests = async (facilityId: number): Promise<TermOutRequestRow[]> =>
  (await axios.get<TermOutRequestRow[]>(ABF_API_ROOT + '/facilities/' + facilityId + '/structure/term-out', { params: { _: Date.now() } })).data;
export const requestTermOut = async (facilityId: number, effectiveDate: string, notes: string, sourceClause?: string): Promise<TermOutRequestRow> =>
  (await axios.post<TermOutRequestRow>(ABF_API_ROOT + '/facilities/' + facilityId + '/structure/term-out', { effectiveDate, notes, sourceClause })).data;
export const decideTermOut = async (facilityId: number, eventId: number, approve: boolean, notes?: string): Promise<TermOutRequestRow> =>
  (await axios.post<TermOutRequestRow>(ABF_API_ROOT + '/facilities/' + facilityId + '/structure/term-out/' + eventId + '/decide', { approve, notes })).data;

export const getAccrualBasis = async (facilityId: number): Promise<AccrualBasis> =>
  (await axios.get<AccrualBasis>(ABF_API_ROOT + '/facilities/' + facilityId + '/accrual-basis', { params: { _: Date.now() } })).data;
export const setAccrualBasis = async (facilityId: number, basis: string, sourceClause: string): Promise<AccrualBasis> =>
  (await axios.put<AccrualBasis>(ABF_API_ROOT + '/facilities/' + facilityId + '/accrual-basis', { basis, sourceClause })).data;

